// Pure utility functions for invoice operations — no DB dependencies.

import type { InvoiceRow, InvoiceLinkWithInvoice, InvoiceLink } from '@shared/types/invoice.ts';
import type { TransactionSide } from './InvoiceReconcileService.ts';
import { round2, isZero, MONEY_TOLERANCE } from '../../calc.ts';
import Big from 'big.js';

// ── Line-item display predicates ────────────────────────────────────────

/**
 * Is this line item net-zero (correction that cancels the original)?
 * Zero-net items are shown dimmed / disabled in the UI and skip click handlers.
 */
export function isLineItemZero(
  netAmount: number | null | undefined,
  tolerance: number = MONEY_TOLERANCE,
): boolean {
  return netAmount == null || Math.abs(netAmount) < tolerance;
}

/**
 * Is this line item an active correction (has delta AND non-zero net)?
 * Pure correction lines that zero out are handled by `isLineItemZero` instead.
 */
export function isLineItemCorrection<T extends { net_amount: number | null; delta?: unknown }>(
  pricing: T,
  tolerance: number = MONEY_TOLERANCE,
): boolean {
  return !!pricing.delta && !isLineItemZero(pricing.net_amount, tolerance);
}

// ── Vendor / side helpers ───────────────────────────────────────────────

/** Extract vendor company ID from invoice direction.
 *  Received → seller is the vendor. Issued → buyer is the vendor. */
export function getVendorId(
  invoice: Pick<InvoiceRow, 'direction' | 'seller_company_id' | 'buyer_company_id'>,
): number | null {
  return invoice.direction === 'received'
    ? invoice.seller_company_id
    : invoice.buyer_company_id;
}

/** Map a reconcile candidate view row to a TransactionSide. */
export function candidateToTransactionSide(c: InvoiceLink): TransactionSide {
  if (c.entity_type === 'transport') {
    return { transaction_id: c.transaction_id, side: 'transport', transport_id: c.entity_id };
  }
  const side = c.side === 'load' || c.side === 'unload' ? c.side : 'load'; // default to load if unexpected
  return { transaction_id: c.transaction_id, side, transport_id: null };
}

// ── Withdrawn invoice detection ─────────────────────────────────────────

/**
 * Detect invoices fully reversed by corrections (original + correction gross ≈ 0).
 * Uses invoice.gross_amount from the joined invoice metadata.
 */
export function getWithdrawnInvoiceIds(links: InvoiceLinkWithInvoice[]): Set<number> {
  const invoiceGross = new Map<number, { invoice: NonNullable<InvoiceLinkWithInvoice['invoice']>; gross: number }>();
  for (const link of links) {
    const inv = link.invoice;
    if (!inv) continue;
    if (!invoiceGross.has(inv.id)) {
      invoiceGross.set(inv.id, { invoice: inv, gross: inv.gross_amount ?? 0 });
    }
  }

  const ids = new Set<number>();
  for (const [, { invoice: inv, gross: origGross }] of invoiceGross) {
    if (inv.invoice_type !== 'original') continue;
    const corrEntries = [...invoiceGross.values()].filter(e => e.invoice.correction_of_invoice_id === inv.id);
    const corrGross = corrEntries.reduce((s, e) => s.plus(e.gross), new Big(0)).toNumber();
    if (isZero(round2(origGross + corrGross))) {
      ids.add(inv.id);
      for (const [, e] of invoiceGross) {
        if (e.invoice.correction_of_invoice_id === inv.id) ids.add(e.invoice.id);
      }
    }
  }
  return ids;
}

